/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, PlusCircle, FolderOpen, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import ContextWarning from '../components/ContextWarning';
import { usePermissions } from '../context/PermissionContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ all: 0, completed: 0, created: 0, in_progress: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { can } = usePermissions();

  const fetchDashboardData = useCallback(async () => {
    if (!can.assignments.view()) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const [countsRes] = await Promise.all([
        apiClient.get('/assignments/status-counts')
      ]);
      setCounts(countsRes.data);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [can]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const stats = {
    total: counts.all || 0,
    completed: counts.completed || 0,
    created: counts.created || 0,
    inProgress: counts.inProgress || 0,
  };

  const handleStateClick = (status) => {
    navigate('/assignments', { state: { status } });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-4">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your media capture operations</p>
        </div>
        <div className="flex items-center gap-3">
          {can.assignments.view() && (<button
            onClick={fetchDashboardData}
            className="btn btn-square btn-ghost bg-base-content/5 border border-base-content/10 hover:bg-base-content/10 rounded-xl transition-[transform,shadow,background-color,border-color] duration-300"
            title="Refresh"
          >
            <RefreshCw size={18} className="" />
          </button>)}
          {can.assignments.create() && user?.subAccountUID && (
            <button
              onClick={() => navigate('/assignments/new')}
              className="btn-premium"
            >
              <PlusCircle size={18} />
              <span>New Assignment</span>
            </button>
          )}
        </div>
      </div>

      <ContextWarning title="Select a sub-account to create or edit assignments" message="You're viewing all accounts. To create, edit, or delete an assignment, select a sub-account from the switcher in the bottom left" />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 space-y-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full"></div>
          <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading dashboard stats...</p>
        </div>
      ) : can.assignments.view() ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            onClick={() => handleStateClick('All')}
            className="card-premium rounded-xl group border-l-4 border-l-primary cursor-pointer"
          >
            <div className="text-base-content font-bold text-xs uppercase tracking-widest mb-2 opacity-60">Total Assignments</div>
            <div className="text-4xl font-black text-base-content group-hover:text-primary transition-colors uppercase tracking-tight">{stats.total}</div>
          </div>
          <div
            onClick={() => handleStateClick('completed')}
            className="card-premium rounded-xl group border-l-4 border-l-success cursor-pointer"
          >
            <div className="text-base-content font-bold text-xs uppercase tracking-widest mb-2 opacity-60">Completed</div>
            <div className="text-4xl font-black text-base-content group-hover:text-success transition-colors uppercase tracking-tight">{stats.completed}</div>
          </div>
          <div
            onClick={() => handleStateClick('created')}
            className="card-premium rounded-xl group border-l-4 border-l-info cursor-pointer"
          >
            <div className="text-base-content font-bold text-xs uppercase tracking-widest mb-2 opacity-60">Created</div>
            <div className="text-4xl font-black text-base-content group-hover:text-info transition-colors uppercase tracking-tight">{stats.created}</div>
          </div>
          <div
            onClick={() => handleStateClick('inProgress')}
            className="card-premium rounded-xl group border-l-4 border-l-warning cursor-pointer"
          >
            <div className="text-base-content font-bold text-xs uppercase tracking-widest mb-2 opacity-60">In Progress</div>
            <div className="text-4xl font-black text-base-content group-hover:text-warning transition-colors uppercase tracking-tight">{stats.inProgress}</div>
          </div>
        </div>
      ) : (<></>)}

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg mb-4 text-sm font-bold">{error}</div>
      )}

      {(can.assignments.view() || can.templates.view()) && <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-base-content uppercase tracking-tight">Quick Actions</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {can.assignments.view() && <button
            onClick={() => navigate('/assignments')}
            className="flex items-center gap-4 p-4 rounded-xl bg-base-200 hover:bg-base-300 transition-colors border border-base-content/10 group"
          >
            <div className="w-12 h-12 bg-base-100 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
              <FolderOpen className="text-primary" size={24} />
            </div>
            <div className="text-left">
              <p className="font-black text-base-content uppercase tracking-tight">View All Assignments</p>
              <p className="text-xs text-base-content/40 font-bold">Manage your existing workflows</p>
            </div>
          </button>}
          {can.templates.view() && <button
            onClick={() => navigate('/workflows')}
            className="flex items-center gap-4 p-4 rounded-xl bg-base-200 hover:bg-base-300 transition-colors border border-base-content/10 group"
          >
            <div className="w-12 h-12 bg-base-100 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
              <PlusCircle className="text-secondary" size={24} />
            </div>
            <div className="text-left">
              <p className="font-black text-base-content uppercase tracking-tight">Manage Workflows</p>
              <p className="text-xs text-base-content/40 font-bold">Create and edit capture templates</p>
            </div>
          </button>}
        </div>
      </div>}
    </div>
  );
};

export default Dashboard;
